// =============================================================================
// AGENTICED SHARED CONFIG
// =============================================================================
// Centralized configuration for all frontends
// =============================================================================

/**
 * API Configuration
 * All frontends should use this config for API calls
 */
export const config = {
  /**
   * Base URL for the AgenticED API
   * Can be overridden via VITE_API_URL environment variable
   */
  apiBase: typeof import.meta !== 'undefined' && import.meta.env?.VITE_API_URL
    ? import.meta.env.VITE_API_URL
    : '',

  /**
   * API version prefix
   * Note: Server routes are mounted without version prefix (e.g., /api/families not /api/v1/families)
   * Keep empty string for compatibility
   */
  apiVersion: '',

  /**
   * Cache durations in milliseconds
   */
  cache: {
    /** Translation cache: 5 minutes */
    translations: 5 * 60 * 1000,
    /** School data cache: 1 hour */
    schools: 60 * 60 * 1000,
    /** Session cache: 30 minutes */
    session: 30 * 60 * 1000,
  },

  /**
   * Asset paths for branding
   * Uses BASE_URL to work with any agent's base path (e.g., /fafsa/, /parent/, etc.)
   * Note: import.meta.env.BASE_URL is statically replaced by Vite at build time
   */
  assets: {
    /** AgenticED logo - uses Vite's BASE_URL for correct path resolution */
    logo: `${import.meta.env.BASE_URL}agenticed-logo.png`,
    /** AgenticED logo alt text */
    logoAlt: 'AgenticED',
  },
};

/**
 * Get the full API URL for an endpoint
 */
export function getApiUrl(endpoint: string): string {
  const base = config.apiBase.replace(/\/$/, '');
  const path = endpoint.startsWith('/') ? endpoint : `/${endpoint}`;
  return `${base}${path}`;
}

/**
 * Get versioned API URL
 */
export function getVersionedApiUrl(endpoint: string): string {
  return getApiUrl(`/api/${config.apiVersion}${endpoint.startsWith('/') ? endpoint : `/${endpoint}`}`);
}

export type Config = typeof config;
